import type ConferenceSession from '../videocalling_conference/ConferenceSession';
import type { VoidCallback } from './common';

export enum DeviceInputType {
  VIDEO = 'videoinput',
  AUDIO = 'audioinput',
}
export enum JanusEvent {
  PARTICIPANT_JOINED = 'participant_joined',
  PARTICIPANT_LEFT = 'participant_left',
  LOCAL_STREAM = 'local_stream',
  REMOTE_STREAM = 'remote_stream',
  REMOTE_TRACKS_UPDATED = 'remote_tracks_updated',
  DATA_CHANNEL_OPEN = 'data_channel_open',
  DATA_CHANNEL_MESSAGE = 'data_channel_message',
  ERROR = 'error',
}
export enum JanusCallType {
  VIDEO = 'video',
  AUDIO = 'audio',
}
export enum JanusMediaTrackReason {
  CREATED = 'created',
  ENDED = 'ended',
  MUTE = 'mute',
  UNMUTE = 'unmute',
}
export enum ConferenceEvent {
  JOIN = 'join',
  LEFT = 'left',
  SLOW_LINK = 'slow-link',
  REMOTE_TRACKS_UPDATED = 'remote-tracks',
  REMOTE_STREAM = 'remote-stream',
  REMOTE_CONNECTION_STATE = 'remote-connection-state',
  DATA_CHANNEL_OPENED = 'data-channel-opened',
  DATA_CHANNEL_MESSAGE = 'data-channel-message',
  SESSION_CONNECTION_STATE = 'session-connection-state',
  ERROR = 'error',
}

export namespace ConnectyCubeJanus {
  export interface CreateOfferParams {
    tracks: Array<{
      type: string;
      mid?: string;
      recv?: boolean;
      capture?: boolean | MediaStreamTrack;
      replace?: boolean;
    }>;
    customizeSdp?(jsep: unknown): void;
    success?(jsep: unknown): void;
    error?(error: unknown): void;
  }
  export interface VideoRoomPluginHandle {
    createOffer(opts: CreateOfferParams): void;
    send(msg: unknown): void;
    getLocalVolume?(id: unknown, callback: (value: unknown) => void): void;
    handleRemoteJsep?(opts: { jsep: unknown }): void;
    getId?(): string | number;
    detach?(opts: { success?: () => void; error?: (err: unknown) => void }): void;
    isAudioMuted?(): boolean;
    unmuteAudio?(): void;
    muteAudio?(): void;
    isVideoMuted?(): boolean;
    unmuteVideo?(): void;
    muteVideo?(): void;
    data?(opts: { data: unknown; label: unknown; success: (value?: unknown) => void; error: (err?: unknown) => void }): void;
    webrtcStuff?: { pc: RTCPeerConnection };
  }
  export interface RemoteFeed {
    userId?: number;
    stream?: MediaStream;
    tracks?: Record<string, MediaStreamTrack>;
    createAnswer(opts: unknown): void;
    send(msg: unknown): void;
    detach(): void;
    getBitrate?(): unknown;
    getRemoteVolume?(id: unknown, callback: (value: unknown) => void): void;
  }
  export interface BitrateDisplayElement {
    text: ((value?: unknown) => void) | null;
  }
  export interface SuccessErrorCallbacks {
    success?: VoidCallback;
    error?: (error: unknown) => void;
  }
  export interface InitCallbacks extends SuccessErrorCallbacks {
    destroyed?: () => void;
    timeoutSessionCallback?: () => void;
  }
  export interface PluginCallbacks extends SuccessErrorCallbacks {
    localStream?: MediaStream;
    displayName?: string;
    iceState?: (iceState: unknown) => void;
    slowLink?: (uplink: unknown, nacks: unknown) => void;
    consentDialog?: (on: unknown) => void;
    mediaState?: (medium: unknown, on: unknown) => void;
    webrtcState?: (on: unknown) => void;
    oncleanup?: () => void;
  }
  export type Session = ConferenceSession;
  export interface RequestCallbacks extends SuccessErrorCallbacks {
    displayName?: string;
  }
  // Listeners
  export type OnParticipantJoinedListener = (
    session: Session,
    userId: number,
    userName: string,
    isExist: boolean
  ) => void;
  export type OnParticipantLeftListener = (session: Session, ...args: unknown[]) => void;
  export type OnSlowLinkListener = (session: Session, userId: number | null, uplink: unknown, nacks: unknown) => void;
  export type OnRemoteStreamListener = (session: Session, userId: number, stream: MediaStream) => void;
  export type OnRemoteTracksUpdatedListener = (
    session: Session,
    userId: number,
    track: MediaStreamTrack,
    eventType: unknown
  ) => void;
  export type OnRemoteConnectionStateChangedListener = (session: Session, userId: number, iceState: unknown) => void;
  export type OnDataChannelOpenedListener = (session: Session, label: unknown) => void;
  export type OnDataChannelMessageListener = (session: Session, label: unknown, data: unknown) => void;
  export type OnSessionConnectionStateChangedListener = (session: Session, iceState: unknown) => void;
  export type OnErrorListener = (session: Session, error: unknown) => void;
  export type Listeners =
    | OnParticipantJoinedListener
    | OnParticipantLeftListener
    | OnSlowLinkListener
    | OnRemoteStreamListener
    | OnRemoteTracksUpdatedListener
    | OnRemoteConnectionStateChangedListener
    | OnDataChannelOpenedListener
    | OnDataChannelMessageListener
    | OnSessionConnectionStateChangedListener
    | OnErrorListener;
  export type ListenerName =
    | 'onParticipantJoinedListener'
    | 'onParticipantLeftListener'
    | 'onSlowLinkListener'
    | 'onRemoteStreamListener'
    | 'onRemoteTracksUpdatedListener'
    | 'onRemoteConnectionStateChangedListener'
    | 'onDataChannelOpenedListener'
    | 'onDataChannelMessageListener'
    | 'onSessionConnectionStateChangedListener'
    | 'onErrorListener';
}
